import {
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import Card from "@mui/material/Card";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import Paper from "@mui/material/Paper";
import html2pdf from "html2pdf.js";
import { useEffect, useRef, useState } from "react";
import * as React from "react";
import { useHistory } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import Layout from "../../components/layout";
import salonService from "../../services/salonServices";
import RejectionModal from "../RejectionModal";
import { DecryptData, jsonParser } from "../../helpers/helper";

const columns = [
  { id: "sr_no", label: "Sr no.", minWidth: 50, sortable: false },
  {
    id: "serviceType",
    label: "Service Type",
    minWidth: 100,
    sortable: true,
  },
  {
    id: "serviceName",
    label: "Service Name",
    minWidth: 100,
    sortable: true,
  },
  {
    id: "price",
    label: "Price",
    minWidth: 100,
    sortable: true,
  },
  {
    id: "description",
    label: "Description",
    minWidth: 100,
    sortable: true,
  },
  {
    id: "details",
    label: "Details",
    minWidth: 100,
    sortable: false,
  },
];

export default function ServiceList() {
  const history = useHistory();
  const toggleContainer = useRef<HTMLDivElement | null>(null);

  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [sortBy, setSortBy] = useState<string>("");
  const [tableData, setTableData] = useState([]);
  const [selectedServiceType, setSelectedServiceType] = useState();
  const [serviceTypes, setServiceType] = useState([]);

  // pagination
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = React.useState(0);
  const currentPage = 1;
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const range = [];
  const minRange = Math.max(1, currentPage - 2);
  const maxRange = Math.min(totalPages, currentPage + 2);
  for (let i = minRange; i <= maxRange; i++) {
    range.push(i);
  }
  const indexOfLastItem = (page + 1) * rowsPerPage;
  const indexOfFirstItem = indexOfLastItem - rowsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const handleSort = (columnId: string) => {
    if (columnId === "sr_no" || columnId === "details") return; // Skip sorting for these columns
    if (sortBy === columnId) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(columnId);
      setSortOrder("asc");
    }
  };

  const sortedItems = [...currentItems].sort((a, b) => {
    if (sortBy === "sr_no" || sortBy === "details") return 0; // Skip sorting for these columns
    // Handle date sorting
    if (sortBy === "fromDate") {
      const dateA = new Date(a.fromDate);
      const dateB = new Date(b.fromDate);

      return sortOrder === "asc"
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    }
    if (sortBy === "to_date") {
      const dateA = new Date(a.to_date);
      const dateB = new Date(b.to_date);

      return sortOrder === "asc"
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    }

    if (a[sortBy] < b[sortBy]) return sortOrder === "asc" ? -1 : 1;
    if (a[sortBy] > b[sortBy]) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const handleClickOutside = (event: MouseEvent) => {
    if (
      toggleContainer.current &&
      !toggleContainer.current.contains(event.target as Node)
    ) {
      setIsModalOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleChange = (event) => {
    const searchTerm = event.target.value.toLowerCase();
    setSearchTerm(searchTerm);

    if (!searchTerm) {
      setFilteredData(tableData);
      return;
    }

    const filtered = tableData.filter((data) => {
      // Check service name
      const matchesServiceName = data.serviceName
        .toLowerCase()
        .includes(searchTerm);

      // Check service type name
      const matchesServiceType = data.name?.toLowerCase().includes(searchTerm);

      // Check variations
      const matchesVariations = data.variations.some((v) => {
        const matchGroupName = v.name.toLowerCase().includes(searchTerm);
        const matchOption = v.options.some((o) =>
          o.name.toLowerCase().includes(searchTerm)
        );
        return matchGroupName || matchOption;
      });

      return matchesServiceName || matchesServiceType || matchesVariations;
    });

    setFilteredData(filtered);
  };

  const handleSelectedServiceType = (event) => {
    const { value } = event.target;
    setSelectedServiceType(value);

    if (value === "all") {
      setFilteredData(tableData); // Show all data if "All" is selected
    } else {
      const filtered = tableData.filter((item) => item.name === value);
      setFilteredData(filtered);
    }
  };

  //code to fetch service types and service data
  const fetchServiceData = async () => {
    const service = await salonService.findAll();
    const decryptDataServiceType = await DecryptData(service.serviceType);
    const parsedDataServiceType = jsonParser(decryptDataServiceType);

    const decryptedServiceTypes = parsedDataServiceType.map((data) => ({
      ...data,
      serviceName: DecryptData(data.serviceName),
    }));

    setServiceType(decryptedServiceTypes);
    const decryptData = await DecryptData(service.serviceData);
    const parsedData = jsonParser(decryptData);
    const servicesData = parsedData.map((data) => {
      if (data.id === 375) {
        console.log(data);
      }

      return {
        id: data.id,
        relatedService: data.relatedService,
        name: data.serviceTypeId ? DecryptData(data.name) : "Deal",
        serviceName: DecryptData(data.serviceName),
        price: DecryptData(data.price),
        description: data.description ? DecryptData(data.description) : null,
        variations: (data.variations || []).map((v) => ({
          name: DecryptData(v.name),
          options: v.options.map((o) => ({
            name: DecryptData(o.name),
            price: DecryptData(o.price),
          })),
        })),
      };
    });
    console.log(servicesData);
    setFilteredData(servicesData);
    setTableData(servicesData);
  };

  //fetching the data when page render
  useEffect(() => {
    fetchServiceData();
  }, []);

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentId(null);
  };

  const closeModalWithDelete = async () => {
    setIsModalOpen(false);
    setCurrentId(null);
    await salonService.delete(currentId);
    fetchServiceData();
  };

  const handleServiceView = async (id) => {
    history.push(`/services/${id}`);
  };

  const handleEditService = async (id) => {
    history.push(`/service/edit/${id}`);
  };

  const handleDeleteService = async (id) => {
    setIsModalOpen(true);
    setCurrentId(id);
  };

  function addNewService() {
    history.push("/service/add");
  }

  function addNewServiceDeal() {
    history.push("/services/deals");
  }

  function handleDownloadPdf() {
    const table = document.getElementById("your-table-id");

    if (table) {
      const container = document.createElement("div");
      container.style.width = "190mm";
      container.style.padding = "10mm";
      const header = document.createElement("div");
      header.innerHTML = `
       <img src="/logo.png" style="display: block; margin: 0 auto; max-width: 180mm; width: 100%; height: auto;" />
     `;
      container.appendChild(header);

      const beforeTable = document.createElement("div");
      beforeTable.innerHTML = '<p style="text-align: center;"></p>';
      container.appendChild(beforeTable);

      const clonedTable = table.cloneNode(true) as HTMLElement;

      const rows = clonedTable.querySelectorAll("tr");
      rows.forEach((row) => {
        const lastCell = row.lastElementChild;
        lastCell?.remove();
      });

      container.appendChild(clonedTable);

      const afterTable = document.createElement("div");
      afterTable.innerHTML =
        '<div style="margin-top:5%;display:flex;flex-direction:row;justify-content:space-around;border-top:1px solid black"><div style="width:30%"><p style="color:#444444">2nd Floor, Plaza 147, Central Commercial, Bahria Orchard, Lahore, 55150, Pakistan</p></div><div><p style="color:#444444">elegancemakeupstudioo@gmail.com</p></div><div><p style="color:#444444">+92 331 555 5933</p></div></div>';
      container.appendChild(afterTable);

      const footer = document.createElement("div");
      footer.innerHTML = '<p style="height:20px"><br></p>';
      container.appendChild(footer);

      // Set CSS styles to avoid content overlap
      const style = document.createElement("style");
      style.innerHTML = `
           .page-break {
             page-break-before: always;
           }
           .container {
             margin-bottom: 50px;
             width: 100%;
             box-sizing: border-box;
           }
           table {
             width: 100%;
             border-collapse: collapse;
             table-layout: fixed; /* Prevents overflow */
           }
           td, th {
             word-wrap: break-word;
             font-size: 10px; /* Optional: reduce font size for better fit */
           }
           tr {
             page-break-inside: avoid;
           }
         `;
      container.appendChild(style);

      // Add a page break to avoid overlap
      const pageBreak = document.createElement("div");
      pageBreak.className = "page-break";
      container.appendChild(pageBreak);

      const htmlString = container.innerHTML;

      html2pdf()
        .from(htmlString)
        .set({
          margin: 10,
          filename: "user.pdf",
          html2canvas: { scale: 2 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .save();
    }
  }

  return (
    <Layout>
      <div className="leavesIndex">
        <div className="leavesIndex__header" style={{ paddingBottom: 0 }}>
          <div className="leavesIndex__header_left">
            <h4 className="attendance_overview_heading">Services</h4>
          </div>
        </div>

        <div className="leavesIndex__header">
          <div className="leavesIndex__header_left">
            <div className="remaingLeavesCounter">
              <div className="remaingLeavesCounterEachAttendance">
                <div
                  className={`searchNameFilterParent ${
                    isFocused ? "searchNameFilterParentFocused" : ""
                  }`}
                  style={{ marginRight: "1vw" }}
                >
                  <FormControl variant="standard">
                    <OutlinedInput
                      onChange={handleChange}
                      value={searchTerm}
                      className="searchNameFilter"
                      placeholder="Search by service name"
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      endAdornment={
                        <InputAdornment position="end">
                          <SearchIcon />
                        </InputAdornment>
                      }
                    />
                  </FormControl>
                </div>
              </div>
              <div className="remaingLeavesCounterEach inventoryFilterFields">
                <div className="singleInputField singleInputFieldFilter">
                  <select
                    name="leave_type"
                    id="leave_type"
                    className="singleInputFilter"
                    onChange={handleSelectedServiceType}
                    value={selectedServiceType}
                  >
                    <option value="" disabled style={{ display: "none" }}>
                      Search By Service Type
                    </option>
                    {serviceTypes.map((data, index) => [
                      <option key={index + 1} value={data.serviceName}>
                        {data.serviceName}
                      </option>,
                    ])}
                    <option value="all">All</option>
                  </select>
                </div>
              </div>

              <div
                className="remaingLeavesCounterEachInventoryButton"
                style={{ display: "flex", justifyContent: "flex-end" }}
              >
                <button
                  onClick={addNewServiceDeal}
                  className="downloadPDFButton downloadPDFButton2 inventoryFilterButtons"
                >
                  Add New Deal
                </button>

                <button
                  onClick={addNewService}
                  className="downloadPDFButton downloadPDFButton2 inventoryFilterButtons"
                >
                  Add New Service
                </button>

                <button
                  onClick={handleDownloadPdf}
                  className="downloadPDFButton downloadPDFButton2"
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
        <Card className="myLeaves__card" variant="outlined">
          <div>
            <Paper sx={{ width: "100%", overflow: "hidden" }}>
              <TableContainer>
                <Table
                  stickyHeader
                  aria-label="sticky table"
                  id="your-table-id"
                >
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          style={{ textAlign: "center" }}
                        >
                          {column.sortable ? (
                            <TableSortLabel
                              active={sortBy === column.id}
                              direction={
                                sortBy === column.id ? sortOrder : "asc"
                              }
                              onClick={() => handleSort(column.id)}
                            >
                              {column.label}
                            </TableSortLabel>
                          ) : (
                            column.label
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedItems.map((row, index) => {
                      const serialNumber = indexOfFirstItem + index + 1;
                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={serialNumber}
                        >
                          <TableCell
                            style={{
                              textAlign: "center",
                            }}
                          >
                            {serialNumber}
                          </TableCell>
                          <TableCell
                            style={{
                              textAlign: "center",
                            }}
                          >
                            {row["name"]}
                          </TableCell>
                          <TableCell
                            style={{
                              textAlign: "center",
                            }}
                          >
                            {row["serviceName"]}
                          </TableCell>
                          <TableCell style={{ textAlign: "center" }}>
                            {row.variations?.length === 0 && (
                              <div style={{ fontWeight: 600 }}>
                                PKR {row.price}
                              </div>
                            )}

                            {row.variations?.length > 0 && (
                              <div
                                style={{
                                  marginTop: 6,
                                  fontSize: 12,
                                  color: "#555",
                                }}
                              >
                                {row.variations.map((v, i) => (
                                  <div key={i} style={{ marginBottom: 4 }}>
                                    <strong>{v.name}</strong>
                                    {v.options.map((o, j) => (
                                      <div key={j} style={{ paddingLeft: 10 }}>
                                        • {o.name} — PKR {o.price}
                                      </div>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            )}
                          </TableCell>

                          <TableCell
                            style={{
                              textAlign: "center",
                            }}
                          >
                            {row["description"]
                              ? row["description"].length > 50
                                ? row["description"].substring(0, 50) + "..."
                                : row["description"]
                              : "--"}
                          </TableCell>
                          <TableCell style={{ textAlign: "center" }}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "center",
                              }}
                            >
                              {/* view */}
                              <div>
                                <button
                                  style={{
                                    border: "none",
                                    color: "#0066ff",
                                    backgroundColor: "transparent",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleServiceView(row["id"])}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="26"
                                    height="26"
                                    viewBox="0 0 26 26"
                                  >
                                    <g data-name="Group 295">
                                      <path
                                        data-name="visibility_FILL0_wght300_GRAD0_opsz24 (1)"
                                        d="M13.003 16.95a4.236 4.236 0 0 0 3.115-1.284 4.248 4.248 0 0 0 1.281-3.117 4.236 4.236 0 0 0-1.284-3.115 4.248 4.248 0 0 0-3.117-1.281 4.236 4.236 0 0 0-3.115 1.284 4.248 4.248 0 0 0-1.281 3.117 4.236 4.236 0 0 0 1.284 3.115 4.248 4.248 0 0 0 3.117 1.281zm0-1.485a2.809 2.809 0 0 1-2.063-.85 2.809 2.809 0 0 1-.85-2.063 2.809 2.809 0 0 1 .85-2.063 2.809 2.809 0 0 1 2.063-.85 2.809 2.809 0 0 1 2.063.85 2.809 2.809 0 0 1 .85 2.063 2.809 2.809 0 0 1-.85 2.063 2.809 2.809 0 0 1-2.065.85zm0 4.639a11.281 11.281 0 0 1-6.231-1.817 13.565 13.565 0 0 1-4.509-4.782 1.9 1.9 0 0 1-.2-.468 1.892 1.892 0 0 1-.062-.484 1.884 1.884 0 0 1 .062-.484 1.9 1.9 0 0 1 .2-.468 13.564 13.564 0 0 1 4.509-4.782 11.281 11.281 0 0 1 6.229-1.82 11.281 11.281 0 0 1 6.231 1.818 13.564 13.564 0 0 1 4.514 4.782 1.9 1.9 0 0 1 .2.468 1.893 1.893 0 0 1 .062.484 1.885 1.885 0 0 1-.062.484 1.9 1.9 0 0 1-.2.468 13.565 13.565 0 0 1-4.509 4.782 11.281 11.281 0 0 1-6.236 1.814zm-.002-7.552zm0 5.934a10.3 10.3 0 0 0 5.6-1.6 10.543 10.543 0 0 0 3.9-4.329 10.544 10.544 0 0 0-3.9-4.329 10.3 10.3 0 0 0-5.6-1.6 10.3 10.3 0 0 0-5.6 1.6 10.544 10.544 0 0 0-3.9 4.329 10.543 10.543 0 0 0 3.9 4.329 10.3 10.3 0 0 0 5.6 1.6z"
                                      />
                                      <path
                                        data-name="Rectangle 293"
                                        fill="none"
                                        d="M0 0h26v26H0z"
                                      />
                                    </g>
                                  </svg>
                                </button>
                              </div>
                              {/* edit */}

                              <div style={{ margin: "0 0.5vw" }}>
                                <button
                                  style={{
                                    border: "none",
                                    color: "#0066ff",
                                    backgroundColor: "transparent",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    if (
                                      !row["relatedService"] ||
                                      row["relatedService"].length === 0
                                    ) {
                                      handleEditService(row["id"]);

                                      // Redirect to a different page if relatedService is null/empty
                                    } else {
                                      // Run your existing edit function
                                      history.push(`/deals/edit/${row["id"]}`);
                                    }
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 26 26"
                                  >
                                    <g data-name="Group 303">
                                      <path d="M4.099 24a2.027 2.027 0 0 1-1.49-.61 2.027 2.027 0 0 1-.61-1.49V6.352a2.027 2.027 0 0 1 .61-1.49 2.027 2.027 0 0 1 1.49-.61h7.96a.785.785 0 0 1 .653.274.939.939 0 0 1 .218.6.917.917 0 0 1-.227.6.811.811 0 0 1-.662.274H4.099a.342.342 0 0 0-.246.112.342.342 0 0 0-.112.246v15.548a.342.342 0 0 0 .112.246.342.342 0 0 0 .246.112h15.548a.342.342 0 0 0 .246-.112.342.342 0 0 0 .112-.246V13.89a.785.785 0 0 1 .274-.653.939.939 0 0 1 .6-.218.939.939 0 0 1 .6.218.785.785 0 0 1 .274.653v8.015a2.027 2.027 0 0 1-.61 1.49 2.027 2.027 0 0 1-1.49.61zm7.775-9.874zm-2.9 1.854v-2.069a2.062 2.062 0 0 1 .163-.808 2.084 2.084 0 0 1 .449-.674l9.912-9.912a1.7 1.7 0 0 1 .594-.394 1.83 1.83 0 0 1 .659-.124 1.778 1.778 0 0 1 .657.124 1.678 1.678 0 0 1 .574.383l1.459 1.454a1.98 1.98 0 0 1 .388.6 1.713 1.713 0 0 1 .135.661 1.881 1.881 0 0 1-.115.65 1.609 1.609 0 0 1-.385.585l-9.946 9.946a2.209 2.209 0 0 1-.674.458 1.97 1.97 0 0 1-.808.172h-2.013a1.017 1.017 0 0 1-.749-.3 1.017 1.017 0 0 1-.304-.752zM22.239 5.23 20.78 3.738zM10.716 15.287h1.448l7.24-7.24-.724-.724-.775-.746-7.189 7.189zm7.964-7.964-.775-.746.775.746.724.724z" />
                                      <path
                                        data-name="Rectangle 294"
                                        fill="none"
                                        d="M0 0h26v26H0z"
                                      />
                                    </g>
                                  </svg>
                                </button>
                              </div>

                              {/* delete */}

                              <div>
                                <button
                                  style={{
                                    border: "none",
                                    color: "#0066ff",
                                    backgroundColor: "transparent",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleDeleteService(row["id"])}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 26 26"
                                  >
                                    <g data-name="Group 302">
                                      <path
                                        data-name="delete_FILL0_wght300_GRAD0_opsz24 (1)"
                                        d="M6.658 24a2.267 2.267 0 0 1-1.663-.692 2.268 2.268 0 0 1-.692-1.663V5.107h-.326a.945.945 0 0 1-.7-.281.946.946 0 0 1-.281-.7.944.944 0 0 1 .281-.7.946.946 0 0 1 .7-.281h4.886a1.111 1.111 0 0 1 .337-.816 1.111 1.111 0 0 1 .816-.337h5.513a1.111 1.111 0 0 1 .816.337 1.111 1.111 0 0 1 .337.816h4.886a.945.945 0 0 1 .7.281.947.947 0 0 1 .281.7.945.945 0 0 1-.281.7.946.946 0 0 1-.7.281h-.326v16.538a2.268 2.268 0 0 1-.692 1.663 2.267 2.267 0 0 1-1.663.692zM19.287 5.107H6.257v16.538a.39.39 0 0 0 .113.288.39.39 0 0 0 .288.113h12.228a.39.39 0 0 0 .288-.113.39.39 0 0 0 .113-.288zm-8.92 14.333a.944.944 0 0 0 .7-.281.946.946 0 0 0 .281-.7V8.687a.945.945 0 0 0-.281-.7.946.946 0 0 0-.7-.281.945.945 0 0 0-.7.281.946.946 0 0 0-.281.7v9.772a.946.946 0 0 0 .281.7.946.946 0 0 0 .7.281zm4.811 0a.945.945 0 0 0 .7-.281.946.946 0 0 0 .281-.7V8.687a.945.945 0 0 0-.281-.7.946.946 0 0 0-.7-.281.945.945 0 0 0-.7.281.946.946 0 0 0-.281.7v9.772a.945.945 0 0 0 .281.7.946.946 0 0 0 .7.281zM6.257 5.107z"
                                      />
                                      <path
                                        data-name="Rectangle 292"
                                        fill="none"
                                        d="M0 0h26v26H0z"
                                      />
                                    </g>
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 100]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          </div>
        </Card>

        <RejectionModal isOpen={isModalOpen} onClose={closeModal}>
          <div className="modal__popup">
            <div className="Portal-Login-Form">
              <div
                className="ValidationModal"
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span className="approvalText">
                  Are you sure that you want to delete that Service?
                </span>

                <div className="CloseRejectionModalButton">
                  <button
                    type="button"
                    className="CloseRejectionModal"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rejectButtonValidationModal"
                    onClick={closeModalWithDelete}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </RejectionModal>
      </div>
    </Layout>
  );
}
